import type { PharmacyMarker } from "./pharmacy-map.types";
import type { VendorsFilteredItem } from "./vendors-filtered.contract";

/**
 * Maps a getVendorsFiltered item onto a map marker.
 *
 * Returns null for vendors the map cannot place: missing coordinates (the
 * query left-joins the location table) or a missing name.
 */
export const mapVendorToMarker = (
	item: VendorsFilteredItem,
): PharmacyMarker | null => {
	if (item.lat === null || item.lng === null || item.name == null) {
		return null;
	}
	const address = item.address;
	const street = [address?.addressLineOne, address?.addressLineTwo]
		.filter(Boolean)
		.join(", ");
	return {
		// PharmacyMarker and usePharmacySelection both key on a numeric id, while
		// the query returns `vendor.id::text`.
		id: Number(item.id),
		name: item.name,
		lat: item.lat,
		lng: item.lng,
		// Meters, and absent whenever the query resolved no location (FR-040).
		distance: item.distance ?? undefined,
		deliveryMethods: item.deliveryMethods
			.filter((method) => method.enabled)
			.map((method) => method.kind),
		featured: item.isTop ?? false,
		...(street || address?.postalCode || address?.city
			? {
					address: {
						street,
						postalCode: address?.postalCode ?? undefined,
						city: address?.city ?? undefined,
					},
				}
			: {}),
	};
};

/** Maps a page of items to markers, dropping the ones without coordinates. */
export const mapVendorsToMarkers = (
	items: readonly VendorsFilteredItem[],
): PharmacyMarker[] =>
	items
		.map(mapVendorToMarker)
		.filter((marker): marker is PharmacyMarker => marker !== null);
